// The "Cue" wire format (see backend/protocol/wire.go,
// backend/README.md's "Cue / WebSocket signaling protocol" section, and
// ios/zmist/Models/CueWire.swift for the Swift port this mirrors). Plain
// ASCII text, no binary framing:
//
//   <securityKey>:<base64(AES-256-CTR(payload))>
//
// where the decrypted payload is:
//
//   <messageNo>:<command>?<channelId>.<groupNo><operation><value>
//
// A reply/relay line back from the server is:
//
//   <status> <messageNo>\n              (ack, no value)
//   <status> <messageNo>:<value>\n      (ack with a value, e.g. a session id)
//   <command>?<channelId>.<groupNo><operation><value>   (a relayed message -
//                                                         no status prefix)

/** Cue command values - single/double-letter strings, no enum on the wire. */
export const CueCommand = {
  sessionOpen: 's',
  sessionHeartbeat: 'su',
  sessionClose: 'sc',
  channelJoin: 'c',
  message: 'm',
  // "message, from a template signal" - value is a TemplateSignal.number
  // instead of free text (see buildTemplateSignalValue below and
  // backend/protocol/wire.go's SplitMessageValue) - the server resolves
  // the preset and relays its name/emoji/value, so nothing about the
  // preset needs to be spelled out client-side.
  templateSignal: 'mt',
  // "message reply" - an ordinary message whose value additionally
  // carries the id of the message it's anchored to (see buildReplyValue
  // below and backend/protocol/wire.go's SplitReplyValue). Repurposed
  // from a command letter the ported old backend never gave distinct
  // handling in this codebase (see wire.go's Action doc comment).
  reply: 'mr',
  // Reveal-round control (planning poker / hidden voting) - value is the
  // action: "open" / "reveal" / "hide" / "close". Owner-only server-side
  // (backend/protocol/redis.go's ChannelSendAuth kind "round"); persisted
  // + relayed as a type "round" message so history and every client's
  // deriveRound (lib/round.ts) stay in sync. See triage_message.wsl.
  round: 'r',
  // AI report job status - value is compact JSON {id, s: status, m?: messageId,
  // e?: error}. Fanned by cmd/report-worker as a job moves queued -> running
  // -> done/failed/stopped, so the owner's Reports screen updates live. Not a
  // stored message (no persistence, no push - empty msgType server-side).
  reportJob: 'rj',
  // ---- server-originated channel/membership events (backend/internal/
  // protocol/emit.go) - the clients NEVER send these, only receive them.
  // Value is a compact JSON hint - "{}", or `{"k":"<kind>"}` for "cu"/"cc"
  // (see ChannelUpdateKind/parseChannelUpdateKind below); still just a
  // hint the handler is free to ignore, same as reportJob. Emitted by the
  // REST channel/membership workflows right after a durable write, so an
  // edit/invite/permission change on one device (or the web) shows up
  // live on the others.
  // "cu" - this channel changed: profile / member-permission defaults /
  //        members / owners / share link / boost / discoverability, or its
  //        chat was cleared. Delivered to the channel's room. channelId is
  //        the nickname.
  channelUpdate: 'cu',
  // "cc" - this channel was deleted. Delivered to the channel's room.
  channelClose: 'cc',
  // "cl" - YOUR channel list changed: you were added / removed / approved /
  //        made a co-owner, a co-owned channel was created or deleted, or
  //        you followed / unfollowed. Delivered to your personal user room
  //        ("cue:room:user:<userId>:1", which the clients join on connect).
  //        channelId on the wire is "user:<userId>".
  channelList: 'cl',
  // "ci" - YOUR pending channel-invites list changed. Delivered to your
  //        personal user room, same as "cl".
  channelInvites: 'ci',
  // "pu" - YOUR profile / settings changed server-side: an admin edited
  //        your tier / feature flags / limits / verified state /
  //        notification prefs in ZMAP (backend/workflows/zmap/users/*).
  //        Delivered to your personal user room; the client re-pulls
  //        GET /profile so it applies without a manual refresh.
  profileUpdate: 'pu',
} as const;

/**
 * The "k" hint carried in a "cu"/"cc" value (backend/internal/protocol/
 * emit.go's EmitChannelEvent `kind` param) - which specific workflow
 * triggered this change. Deliberately typed as `string`, not a closed
 * union: an older server (mid-deploy) sends none at all, and this list is
 * just documentation of what the backend currently emits, not a contract
 * the client should assume is exhaustive - parseChannelUpdateKind below
 * returns '' for anything it doesn't recognize, and callers already treat
 * an unrecognized kind as "use the safe default", not an error.
 */
export const ChannelUpdateKind = {
  update: 'update',
  memberAdd: 'member_add',
  memberRemove: 'member_remove',
  memberApprove: 'member_approve',
  memberPermissions: 'member_permissions',
  ownerAdd: 'owner_add',
  ownerRemove: 'owner_remove',
  boost: 'boost',
  shareLinkEnable: 'share_link_enable',
  shareLinkDisable: 'share_link_disable',
  shareLinkPassword: 'share_link_password',
  shareLinkRequestToJoin: 'share_link_request_to_join',
  searchableEnable: 'searchable_enable',
  searchableDisable: 'searchable_disable',
  inviteAccept: 'invite_accept',
  requestAccess: 'request_access',
  clearMessages: 'clear_messages',
  channelFollow: 'channel_follow',
  channelUnfollow: 'channel_unfollow',
  delete: 'delete',
} as const;

/**
 * Decodes the "k" field out of a "cu"/"cc" value's compact JSON hint
 * ("{}", or `{"k":"<kind>"}`) - '' for "{}", a missing/non-string "k", or
 * anything that fails to parse at all (an older server, or malformed
 * input). Never throws.
 */
export function parseChannelUpdateKind(value: string): string {
  try {
    const parsed = JSON.parse(value) as { k?: unknown };
    return typeof parsed.k === 'string' ? parsed.k : '';
  } catch {
    return '';
  }
}

/**
 * ASCII Unit Separator (0x1F) - reserved for a "templateSignal" ("mt")
 * command's value: "<TemplateSignal.number>" + MESSAGE_VALUE_SEP +
 * "<valueOverride-or-empty>" + MESSAGE_VALUE_SEP + "<replyToId-or-empty>"
 * (backend/protocol/wire.go's MessageValueSep/SplitMessageValue). Never
 * appears in ordinary typed text; "mt" is a brand-new command with no
 * backward-compat constraint.
 */
export const MESSAGE_VALUE_SEP = '\x1f';

/**
 * Builds an "mt" command's value - see MESSAGE_VALUE_SEP. The third,
 * reply-anchor segment is only appended when replyToId is given, so a
 * plain (non-reply) signal keeps the original 2-part shape.
 */
export function buildTemplateSignalValue(number: string, valueOverride?: string, replyToId?: string): string {
  const base = `${number}${MESSAGE_VALUE_SEP}${valueOverride ?? ''}`;
  return replyToId ? `${base}${MESSAGE_VALUE_SEP}${replyToId}` : base;
}

/**
 * Builds an "mr" (message reply) command's value: "<replyToId>" +
 * MESSAGE_VALUE_SEP + "<text>" - see backend/protocol/wire.go's
 * SplitReplyValue. Applied AFTER withMessageId prepends the client
 * message id, same nesting "mt"'s buildTemplateSignalValue uses.
 */
export function buildReplyValue(replyToId: string, text: string): string {
  return `${replyToId}${MESSAGE_VALUE_SEP}${text}`;
}

/**
 * Prepends the client-generated message id to a "m"/"mt" command's value
 * on the wire: "<id>" + MESSAGE_VALUE_SEP + "<rest>" (backend/protocol/
 * wire.go's SplitMessageId). This is what lets a Cue-persisted signal
 * (see backend/workflows/protocol/triage_message.wsl's storage-enqueue
 * step) reconcile with the sender's own optimistically-rendered message
 * instead of the storage stage minting a fresh id server-side.
 */
export function withMessageId(id: string, rest: string): string {
  return `${id}${MESSAGE_VALUE_SEP}${rest}`;
}

/**
 * The operation character in a message payload must be exactly one
 * character from the server's regex class [\s.~!@#$%^&*_+\-=|l]. "." is
 * used as a fixed, semantically-neutral separator for plain chat text.
 */
export const DEFAULT_OPERATION = '.';

/**
 * All clients that should relay to each other need to land in the exact
 * same "channelId:groupNo" room. The server's payload regex only reliably
 * captures a single-character groupNo (a documented parsing quirk
 * inherited from the old backend), so this app standardizes on one fixed
 * room per channel.
 */
export const DEFAULT_GROUP_NO = '1';

export function buildEnvelope(securityKey: string, base64Payload: string): string {
  return `${securityKey}:${base64Payload}`;
}

export interface CueEnvelope {
  securityKey: string;
  base64Payload: string;
}

/** Splits on the *first* ":" only, matching the server's lazy regex. */
export function parseEnvelope(raw: string): CueEnvelope | null {
  const colonIndex = raw.indexOf(':');
  if (colonIndex === -1) return null;
  const securityKey = raw.slice(0, colonIndex);
  const base64Payload = raw.slice(colonIndex + 1);
  if (!securityKey || !base64Payload) return null;
  return { securityKey, base64Payload };
}

/** Builds the plaintext payload for a request: "<no>:<cmd>?<chan>.<grp><op><value>". */
export function buildPayload(opts: {
  messageNo: string;
  command: string;
  channelId: string;
  groupNo?: string;
  operation?: string;
  value?: string;
}): string {
  const { messageNo, command, channelId, groupNo = DEFAULT_GROUP_NO, operation = DEFAULT_OPERATION, value = '' } = opts;
  return `${messageNo}:${command}?${channelId}.${groupNo}${operation}${value}`;
}

/** A decoded reply/ack line: "<status> <messageNo>[:<value>]". */
export interface CueReply {
  status: number;
  messageNo: string;
  value?: string;
}

export function parseReply(text: string): CueReply | null {
  const trimmed = text.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return null;
  const statusText = trimmed.slice(0, spaceIndex);
  if (!/^\d+$/.test(statusText)) return null;
  const rest = trimmed.slice(spaceIndex + 1);
  const colonIndex = rest.indexOf(':');
  if (colonIndex === -1) return { status: Number(statusText), messageNo: rest };
  return { status: Number(statusText), messageNo: rest.slice(0, colonIndex), value: rest.slice(colonIndex + 1) };
}

/**
 * A relayed message from another client in the same room:
 * "<command>?<channelId>.<groupNo><operation><value>" - no status prefix,
 * no messageNo (see backend/protocol/wire.go's RelayBody).
 */
export interface CueRelayedMessage {
  command: string;
  channelId: string;
  groupNo: string;
  value: string;
}

export function parseRelayed(text: string): CueRelayedMessage | null {
  const qIndex = text.indexOf('?');
  if (qIndex === -1) return null;
  const command = text.slice(0, qIndex);
  const body = text.slice(qIndex + 1);
  const dotIndex = body.indexOf('.');
  if (dotIndex === -1) return null;
  const channelId = body.slice(0, dotIndex);
  const rest = body.slice(dotIndex + 1);
  if (!rest) return null;
  const groupNo = rest.slice(0, 1);
  const value = rest.slice(1 + DEFAULT_OPERATION.length);
  return { command, channelId, groupNo, value };
}

/**
 * True for a plaintext pre-authentication status reply ("<digits><space>...")
 * - the shape the server sends before it knows which secret to encrypt
 * under (unknown security key, malformed envelope, decrypt/parse
 * failure). Everything else is base64(AES-CTR(...)) ciphertext, encrypted
 * under the client's own secret. A leading-digit check alone isn't enough
 * to tell the two apart: base64 ciphertext can legitimately start with a
 * digit too (10 of the 64 alphabet characters), which caused a live relay
 * to be silently misclassified and dropped during development. Base64
 * never contains a space, so requiring "digits then a space" is
 * unambiguous.
 */
export function isPlaintextStatusReply(text: string): boolean {
  return /^\d+ /.test(text);
}

/**
 * A reply/relay's decrypted text is either a status ack ("NNN ...") or a
 * relayed message ("cmd?..."). Try the ack shape first since it's
 * unambiguous (must start with digits then a space).
 */
export type CueIncoming = { kind: 'reply'; reply: CueReply } | { kind: 'relayed'; relayed: CueRelayedMessage };

export function parseIncoming(text: string): CueIncoming | null {
  const reply = parseReply(text);
  if (reply) return { kind: 'reply', reply };
  const relayed = parseRelayed(text);
  if (relayed) return { kind: 'relayed', relayed };
  return null;
}
